/* eslint-disable react-refresh/only-export-components */
/**
 * OpsRetrievalFormParts
 *
 * Shared primitives and constants for the OPS retrieval modal panels.
 * Exported: ChipInput, SectionHeading, FieldRow, TextInput,
 *           COUNTRY_OPTIONS, LANGUAGE_OPTIONS, EMPTY_QUERY
 */

import React, { useState } from 'react';
import type { OpsAdvancedQuery } from '../services/opsRetrievalService';

// --- ChipInput ---------------------------------------------------------------

export interface ChipInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

export function ChipInput({ values, onChange, placeholder }: ChipInputProps) {
  const [input, setInput] = useState('');

  const addChip = () => {
    const trimmed = input.trim();
    if (trimmed && !values.includes(trimmed)) onChange([...values, trimmed]);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addChip(); }
    else if (e.key === 'Backspace' && !input && values.length > 0) onChange(values.slice(0, -1));
  };

  return (
    <div className="flex flex-wrap gap-1 border border-slate-300 rounded px-2 py-1 min-h-[2rem] bg-white focus-within:ring-1 focus-within:ring-slate-400">
      {values.map((v) => (
        <span key={v} className="flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-full">
          {v}
          <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} className="text-slate-400 hover:text-slate-700 leading-none">&times;</button>
        </span>
      ))}
      <input
        className="flex-1 min-w-[8rem] outline-none text-sm py-0.5"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={addChip}
        placeholder={values.length === 0 ? placeholder : ''}
      />
    </div>
  );
}

// --- SectionHeading ----------------------------------------------------------

export function SectionHeading({ label }: { label: string }) {
  return (
    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-3 mb-1.5 first:mt-0 border-b border-slate-100 pb-0.5">
      {label}
    </h4>
  );
}

// --- FieldRow ----------------------------------------------------------------

export function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <label className="block text-xs text-slate-500 mb-0.5">{label}</label>
      <div>{children}</div>
    </div>
  );
}

// --- TextInput ---------------------------------------------------------------

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 ${props.className ?? ''}`}
    />
  );
}

// --- Constants ---------------------------------------------------------------

export const COUNTRY_OPTIONS = [
  { code: 'EP', label: 'EP \u2014 European Patent Office' },
  { code: 'US', label: 'US \u2014 United States' },
  { code: 'WO', label: 'WO \u2014 WIPO (PCT)' },
  { code: 'JP', label: 'JP \u2014 Japan' },
  { code: 'CN', label: 'CN \u2014 China' },
  { code: 'KR', label: 'KR \u2014 South Korea' },
  { code: 'DE', label: 'DE \u2014 Germany' },
  { code: 'FR', label: 'FR \u2014 France' },
  { code: 'GB', label: 'GB \u2014 United Kingdom' },
];

export const LANGUAGE_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'EN', label: 'English' },
  { value: 'FR', label: 'French' },
  { value: 'DE', label: 'German' },
];

export const EMPTY_QUERY: OpsAdvancedQuery = {
  keywords_title_abstract: '',
  keywords_title: '',
  keywords_full_text: '',
  inventor: [],
  applicant: [],
  cpc: [],
  ipc: [],
  publication_number: '',
  application_number: '',
  priority_number: '',
  publication_date_from: '',
  publication_date_to: '',
  application_date_from: '',
  application_date_to: '',
  priority_date_from: '',
  priority_date_to: '',
  country_codes: ['EP'],
  language: '',
};
